<script lang="ts">
  import { goto } from "$app/navigation";

  import "@docsearch/css";
  import docsearch from "@docsearch/js";
  import SearchIcon from "@lucide/svelte/icons/search";
  import { render } from "preact";
  import { onMount } from "svelte";

  import Button from "$lib/design-systems/product/primitives/buttons/Button.svelte";

  import "$lib/docs/Search.css";

  interface Props {
    indexName: string;
    placeholder: string;
    onClose?: () => void;
    /**
     * "input" renders a trigger matching `SearchInput`; "docsearch" keeps the
     * stock DocSearch button (for surfaces that restyle it themselves).
     */
    trigger?: "input" | "docsearch";
  }

  let {
    indexName,
    placeholder,
    onClose = undefined,
    trigger = "input",
  }: Props = $props();

  let container: HTMLElement;

  const HOT_KEY = "/";

  function open() {
    container.querySelector<HTMLButtonElement>(".DocSearch-Button")?.click();
  }

  onMount(() => {
    const style = getComputedStyle(container);
    // A hacky way preventing initalizing multiple docsearch() components on the same page.
    // This fixes a bug where we had two search modals and on the docs page.
    if (style.display === "none") {
      return;
    }
    docsearch({
      container,
      appId: "QCT5DT4VBL",
      apiKey: "9cb1f5f5f9d8e20a2597fbd839324093",
      indexName,
      placeholder,
      maxResultsPerGroup: 7,
      searchParameters: {
        hitsPerPage: 10,
      },
      navigator: {
        navigate(item) {
          goto(item.itemUrl);

          if (onClose) {
            onClose();
          }
        },
      },
      transformItems(items) {
        return items.map((item) => {
          return {
            ...item,
            url: item.url.replace(/^https:\/\/modal\.com/, ""),
          };
        });
      },
    });

    return () => {
      // @docsearch/js renders a Preact app that registers a global keydown
      // listener (Cmd+K). Unmounting via Preact's render ensures the listener
      // is cleaned up when navigating away from docs pages.
      render(null, container);
    };
  });
</script>

<div class="docsearch" class:docsearch-hidden-button={trigger === "input"}>
  {#if trigger === "input"}
    <Button
      variant="secondary"
      iconLeading={SearchIcon}
      aria-label={placeholder}
      onclick={open}
      class="p-2 font-normal lg:hidden"
    />
    <div class="hidden lg:block">
      <button
        type="button"
        class="mdli-wrapper relative flex w-64 items-center"
        aria-label={placeholder}
        onclick={open}
      >
        <SearchIcon
          size={16}
          class="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 icon-primary"
        />
        <span class="input w-full truncate text-left text-tertiary">
          {placeholder}
        </span>
        <kbd
          class="pointer-events-none absolute top-1/2 right-3 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded bg-secondary text-xs text-secondary"
        >
          {HOT_KEY}
        </kbd>
      </button>
    </div>
  {/if}
  <div bind:this={container}></div>
</div>

<style lang="postcss">
  @import "$lib/design-systems/product/primitives/inputs/input-styles.css";

  .mdli-wrapper .input {
    @apply pr-9 pl-10;
  }

  /* Keep DocSearch's own button mounted (it owns the ⌘K / `/` listeners) but
     out of view, and let the trigger above open the modal by clicking it. */
  .docsearch-hidden-button :global(.DocSearch-Button) {
    @apply sr-only;
  }
</style>
